import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import rolesServices from './rolesServices';
import type {
  PERMISSION_GROUP,
  ADD_GROUP_PAYLOAD,
  GRP_EMP_DATA,
  UPDATE_GROUP_PAYLOAD,
  ADD_USER,
} from './roles.type';
import type FILTERS from '../../../../common/filters.type';

const initialState = {
  isLoading: false,

  groups: [] as PERMISSION_GROUP[],

  roleEmployeesList: {
    totalDataCount: 0,
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    pageData: [] as GRP_EMP_DATA[],
  },

  totalPermissions: [] as string[],
};

export const rolesList = createAsyncThunk('rolesList', async (payload, thunkAPI) => {
  try {
    const data: AxiosResponse = await rolesServices.roles();

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const addRole = createAsyncThunk('addRole', async (payload: ADD_GROUP_PAYLOAD, thunkAPI) => {
  try {
    const data: AxiosResponse = await rolesServices.addRole(payload);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const updateRole = createAsyncThunk(
  'updateRole',
  async (values: {groupId: number; payload: UPDATE_GROUP_PAYLOAD}, thunkAPI) => {
    try {
      const data: AxiosResponse = await rolesServices.updateRole(values);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const roleEmplyoees = createAsyncThunk(
  'roleEmplyoees',
  async (values: {groupId: number; filters: FILTERS}, thunkAPI) => {
    try {
      const data: AxiosResponse = await rolesServices.roleEmployees(values);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const addInRole = createAsyncThunk('addInRole', async (payload: ADD_USER, thunkAPI) => {
  try {
    const data: AxiosResponse = await rolesServices.addInRole(payload);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const removeFromRole = createAsyncThunk(
  'removeFromRole',
  async (relationId: number, thunkAPI) => {
    try {
      const data: AxiosResponse = await rolesServices.removeFromRole(relationId);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const totalPermissions = createAsyncThunk('totalPermissions', async (load, thunkAPI) => {
  try {
    const data: AxiosResponse = await rolesServices.totalPermissions();

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});

export const rolesSlice = createSlice({
  name: 'rolesSlice',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.roleEmployeesList.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.roleEmployeesList.page_number = 1;
      state.roleEmployeesList.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.roleEmployeesList.page_number = 1;
      state.roleEmployeesList.search = action.payload;
    },
    clearFilters(state) {
      state.roleEmployeesList.page_size = 10;
      state.roleEmployeesList.page_number = 1;
      state.roleEmployeesList.search = '';
      state.roleEmployeesList.sort_by = 'name';
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.roleEmployeesList.ascending = action.payload.ascending;
      state.roleEmployeesList.sort_by = action.payload.sortBy;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(rolesList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(rolesList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.groups = action.payload.data;
    });
    builder.addCase(rolesList.rejected, (state) => {
      state.isLoading = false;
    });

    builder.addCase(addRole.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addRole.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Role added Successfully`);
    });
    builder.addCase(addRole.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });

    builder.addCase(totalPermissions.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(totalPermissions.fulfilled, (state, action) => {
      state.isLoading = false;
      state.totalPermissions = action.payload.data;
    });
    builder.addCase(totalPermissions.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(updateRole.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateRole.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Role updated Successfully`);
    });
    builder.addCase(updateRole.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });

    builder.addCase(roleEmplyoees.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(roleEmplyoees.fulfilled, (state, action) => {
      state.isLoading = false;
      state.roleEmployeesList = action.payload.data;
    });
    builder.addCase(roleEmplyoees.rejected, (state, action) => {
      state.isLoading = false;
      state.roleEmployeesList.pageData = [];
      message.error(`${action.payload}`);
    });

    builder.addCase(addInRole.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addInRole.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`User added in Role Successfully`);
    });
    builder.addCase(addInRole.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(removeFromRole.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(removeFromRole.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`User removed from Role Successfully`);
    });
    builder.addCase(removeFromRole.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });
  },
});
export const {pageNumberChange, pageSizeChange, searchBy, sortBy} = rolesSlice.actions;

export default rolesSlice.reducer;
